import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import FilterHeaderCell, { ActiveFilters } from "../common/FilterHeaderCell";
import { activeFilterCount, filterOptions, matchesColumnFilters } from "../common/columnFilters";
import Header from "../Header/Header";
import ConfirmDialog from "../common/ConfirmDialog";
import DisabledHint from "../common/DisabledHint";
import reduxStore from "../redux";
import { styles } from "../MainPage/MainPage";
import { messages } from "../common/notification";
import { useStoreListRefresh } from "../common/useStoreList";
import { useDashboardStoreDefault } from "../common/useDashboardStore";
import { setDeviceMaster } from "../redux/action/deviceMaster";
import { loadHouseForPicker } from "../DeviceSchedule/api";
import { fetchRegions } from "../StoreRegions/api";
import { parseCsv } from "../StoreRegions/functions";
import { downloadCsv, mapWithConcurrency } from "../Reports/functions";
import { MultiSelect, cellSelectStyles, selectStyles, storeOptionLabel } from "../Reports/selects";
import { pageOf, regionOptions, storesInRegions } from "../StoreAnalytics/functions";
import { fetchCategories, saveCategories, saveScheduleConfig } from "./api";
import { COLUMNS, LOAD_CONCURRENCY, SCHEDULE_OPTIONS } from "./constants";
import {
  applyCsvPlan,
  buildCsvPlan,
  changedFirst,
  DEVICE_SETUP_FIELDS,
  csvRows,
  csvStoreCodes,
  deviceRows,
  matchesSearch,
  SAMPLE_DEVICE_ID,
  sampleCsvRows,
  clearSaved,
  saveRequests,
  setEdit,
  storesWithDevices,
  unsetCount,
  unsetFirst,
  withEdits,
} from "./functions";
import "../Reports/Reports.css";
import "../StoreAnalytics/StoreAnalytics.css";
import "./DeviceMaster.css";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250].map((size) => ({ value: size, label: `${size} rows` }));
const SCHEDULE_SELECT_OPTIONS = SCHEDULE_OPTIONS.map((value) => ({ value, label: value }));

// The header filters: typed for the device's own id and name, picked from a
// list for the columns whose values repeat. Fields are COLUMNS' keys.
const TYPED_FILTERS = ["deviceId", "deviceName"];
const COLUMN_FILTERS = Object.keys(COLUMNS).map((field) => ({
  field,
  kind: TYPED_FILTERS.includes(field) ? "text" : "select",
}));
// Shown on hover over an action that waits for stores' devices to load.
const LOADING_TITLE = "Loading store devices, please wait...";


const plural = (count, word, many = `${word}s`) => `${count} ${count === 1 ? word : many}`;

function DeviceMaster() {
  const dispatch = useDispatch();
  const housesList = useSelector((state) => state.housesList);
  const houses = useSelector((state) => state.mutipleHouse);
  const { edits = {} } = useSelector((state) => state.deviceMaster ?? {});
  const { refreshing: storesRefreshing, refresh: refreshStoreList } = useStoreListRefresh();

  const [regions, setRegions] = useState([]);
  // Device types from GET /v1/asset/categories; null until it answers. These
  // are the only types a device can be given.
  const [categories, setCategories] = useState(null);
  const [categoriesError, setCategoriesError] = useState("");
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [search, setSearch] = useState("");
  // field -> typed text or picked value, for the header filters.
  const [columnFilters, setColumnFilters] = useState({});
  const setColumnFilter = (field, value) =>
    setColumnFilters((current) => ({ ...current, [field]: value }));
  const filterCount = activeFilterCount(columnFilters);
  const [loadingCount, setLoadingCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // A read CSV waiting to be applied: { name, plan, stores }.
  const [csvPlan, setCsvPlan] = useState(null);
  const [readingCsv, setReadingCsv] = useState(false);
  const fileInput = useRef(null);

  // Store codes read this visit, so each is fetched once unless refreshed.
  const loaded = useRef(new Set());

  // Returns the list too, so a CSV read can wait for it.
  const loadCategories = useCallback(async () => {
    const result = await fetchCategories();
    if (result.success) {
      setCategories(result.data);
      setCategoriesError("");
      return result.data;
    }
    setCategoriesError(result.message);
    messages.error(result.message);
    return null;
  }, []);

  useEffect(() => {
    fetchRegions().then((response) => {
      if (response.success) setRegions(response.data);
    });
    loadCategories();
  }, [loadCategories]);

  const stores = useMemo(
    () =>
      [...(housesList ?? [])]
        .filter((store) => store.code)
        .sort((a, b) => (a.houseName || "").localeCompare(b.houseName || "")),
    [housesList]
  );

  const regionOptionList = useMemo(() => regionOptions(stores, regions), [stores, regions]);

  const storeOptions = useMemo(
    () =>
      storesInRegions(stores, selectedRegions.map((option) => option.value)).map((store) => ({
        value: store.code,
        label: store.houseName || store.code,
        property: store,
      })),
    [stores, selectedRegions]
  );

  // Opens on the store picked on the Dashboard.
  useDashboardStoreDefault("deviceMaster", storeOptions, selectedStores, setSelectedStores);

  // Stores outside the picked regions, or gone from the list, are dropped.
  useEffect(() => {
    const available = new Set(storeOptions.map((option) => option.value));
    setSelectedStores((picked) => {
      const kept = picked.filter((option) => available.has(option.value));
      return kept.length === picked.length ? picked : kept;
    });
  }, [storeOptions]);

  const loadStores = useCallback(async (properties, force) => {
    if (properties.length === 0) return;
    setLoadingCount((count) => count + properties.length);
    await mapWithConcurrency(properties, LOAD_CONCURRENCY, async (property) => {
      const result = await loadHouseForPicker(property, { force });
      if (result.success) {
        loaded.current.add(property.houseAccessCode);
      } else {
        // Picked again later, it is retried.
        loaded.current.delete(property.houseAccessCode);
        messages.error(`${property.houseName || property.houseAccessCode}: ${result.message}`);
      }
      setLoadingCount((count) => count - 1);
    });
  }, []);

  // Each newly picked store is read once; the persisted copy is not trusted
  // across visits, since devices may have been added since.
  useEffect(() => {
    const fresh = selectedStores
      .map((option) => option.property)
      .filter((property) => !loaded.current.has(property.houseAccessCode));
    fresh.forEach((property) => loaded.current.add(property.houseAccessCode));
    loadStores(fresh, true);
  }, [selectedStores, loadStores]);

  const refresh = () => {
    loadStores(selectedStores.map((option) => option.property), true);
  };

  const rows = useMemo(
    () => withEdits(deviceRows(selectedStores.map((option) => option.property), houses), edits),
    [selectedStores, houses, edits]
  );

  const types = useMemo(() => categories ?? [], [categories]);
  const typeSelectOptions = useMemo(
    () => types.map((value) => ({ value, label: value })),
    [types]
  );

  const filtered = useMemo(
    () =>
      rows.filter(
        (row) => matchesSearch(row, search) && matchesColumnFilters(row, columnFilters, COLUMN_FILTERS)
      ),
    [rows, search, columnFilters]
  );
  // A select filter's choices come from all the listed rows, not just the ones
  // other filters leave, so picking one filter never empties another's list.
  const columnFilterOptions = useMemo(
    () =>
      Object.fromEntries(
        COLUMN_FILTERS.filter(({ kind }) => kind === "select").map(({ field }) => [
          field,
          filterOptions(rows, field),
        ])
      ),
    [rows]
  );
  // What Save would send, and how many devices that covers.
  const requests = useMemo(() => saveRequests(edits), [edits]);
  const saveCount = useMemo(
    () =>
      new Set(
        [...requests.categoryUpdates, ...requests.scheduleUpdates].map((update) => update.deviceId)
      ).size,
    [requests]
  );
  // Edits for devices in stores not picked right now still count; they are
  // kept until saved or discarded.
  const editCount = Object.keys(edits).length;

  useEffect(() => setPage(1), [search, selectedStores, columnFilters]);

  const updateRow = (row, field, value) => {
    const current = reduxStore.getState().deviceMaster?.edits ?? {};
    dispatch(setDeviceMaster({ edits: setEdit(current, row, field, value) }));
  };

  // Type changes go to /v1/asset/category and scheduling changes to
  // /v1/asset/scheduleConfig: one call each, carrying every device. A call that
  // saves clears its own edits; a failed one keeps them, so Save can simply be
  // pressed again.
  const save = async () => {
    const { categoryUpdates, scheduleUpdates } = saveRequests(
      reduxStore.getState().deviceMaster?.edits ?? {}
    );
    if (categoryUpdates.length === 0 && scheduleUpdates.length === 0) return;

    setSaving(true);
    const [categoryResult, scheduleResult] = await Promise.all([
      saveCategories(categoryUpdates),
      saveScheduleConfig(scheduleUpdates),
    ]);
    setSaving(false);

    let remaining = reduxStore.getState().deviceMaster?.edits ?? {};
    if (categoryResult.success) remaining = clearSaved(remaining, "deviceType", categoryResult.deviceIds);
    if (scheduleResult.success) remaining = clearSaved(remaining, "toBeScheduled", scheduleResult.deviceIds);
    dispatch(setDeviceMaster({ edits: remaining }));

    const savedCount = (result) => (result.success ? result.deviceIds.length : 0);
    const types = savedCount(categoryResult);
    const schedules = savedCount(scheduleResult);
    const parts = [
      types && `device type for ${plural(types, "device")}`,
      schedules && `scheduling for ${plural(schedules, "device")}`,
    ].filter(Boolean);
    if (parts.length) messages.success(`Saved ${parts.join(" and ")}`);

    if (!categoryResult.success) {
      messages.error(`Device type, ${plural(categoryResult.deviceIds.length, "device")}: ${categoryResult.message}`);
    }
    if (!scheduleResult.success) {
      messages.error(`Scheduling, ${plural(scheduleResult.deviceIds.length, "device")}: ${scheduleResult.message}`);
    }

    // Re-read only the stores whose devices were saved. One not picked right
    // now is marked unread, so picking it again fetches it fresh.
    const savedIds = [categoryResult, scheduleResult]
      .filter((result) => result.success)
      .flatMap((result) => result.deviceIds);
    const changed = storesWithDevices(stores, reduxStore.getState().mutipleHouse, savedIds);
    const picked = new Set(selectedStores.map((option) => option.property.houseAccessCode));
    changed
      .filter((store) => !picked.has(store.houseAccessCode))
      .forEach((store) => loaded.current.delete(store.houseAccessCode));
    loadStores(changed.filter((store) => picked.has(store.houseAccessCode)), true);
  };

  // Reads an uploaded CSV into a plan to review before anything changes. The
  // stores it names are loaded first, so a file covering stores not picked on
  // the page still matches their devices; without a DPI ID column it is
  // matched against the picked stores only.
  const readCsv = async (file) => {
    setReadingCsv(true);
    try {
      const parsed = parseCsv(await file.text());
      const codes = new Set(csvStoreCodes(parsed));
      const fileStores = stores.filter((store) => codes.has(String(store.code).toUpperCase()));
      const planStores = fileStores.length
        ? fileStores
        : selectedStores.map((option) => option.property);

      await loadStores(
        planStores.filter((store) => !loaded.current.has(store.houseAccessCode)),
        true
      );

      const state = reduxStore.getState();
      const candidates = withEdits(
        deviceRows(planStores, state.mutipleHouse),
        state.deviceMaster?.edits ?? {}
      );
      // Types are checked against the API's list, so it has to be loaded.
      const typeList = categories ?? (await loadCategories()) ?? [];
      const plan = buildCsvPlan(parsed, { rows: candidates, types: typeList });

      // Codes in the file that are not stores this account can see.
      const missing = [...codes].filter(
        (code) => !fileStores.some((store) => String(store.code).toUpperCase() === code)
      );
      for (const code of missing) {
        plan.errors.unshift({ line: 0, message: `Store ${code} is not in your store list.` });
      }

      setCsvPlan({ name: file.name, plan, stores: planStores });
    } catch (error) {
      console.error("CSV read failed", error);
      messages.error("Could not read the file");
    } finally {
      setReadingCsv(false);
    }
  };

  const onFile = (event) => {
    const file = event.target.files?.[0];
    // Reset so picking the same file again still fires onChange.
    event.target.value = "";
    if (file) readCsv(file);
  };

  const applyCsv = () => {
    if (!csvPlan || csvPlan.plan.updates.length === 0) return;
    const { plan, stores: planStores } = csvPlan;
    const state = reduxStore.getState().deviceMaster ?? {};

    dispatch(setDeviceMaster({ edits: applyCsvPlan(state.edits ?? {}, plan.updates) }));

    // Show the stores the file covered. Their region may be filtered out, in
    // which case the region filter is cleared so they stay picked.
    const picked = new Set(selectedStores.map((option) => option.value));
    const added = planStores
      .filter((store) => !picked.has(store.code))
      .map((store) => ({ value: store.code, label: store.houseName || store.code, property: store }));
    if (added.length) {
      const visible = new Set(storeOptions.map((option) => option.value));
      if (added.some((option) => !visible.has(option.value))) setSelectedRegions([]);
      setSelectedStores((current) => [...current, ...added]);
    }

    messages.success(`Updated ${plural(plan.updates.length, "device")} from ${csvPlan.name}`);
    setCsvPlan(null);
  };

  // Asks first (ConfirmDialog), since discarded edits cannot be got back.
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const discard = () => {
    setConfirmingDiscard(false);
    dispatch(setDeviceMaster({ edits: {} }));
  };

  const download = () => {
    try {
      downloadCsv(csvRows(filtered), "Device Master.csv");
    } catch (error) {
      console.error("CSV download failed", error);
      messages.error("Could not build the file");
    }
  };

  // The devices shown (after the store and search filters), with only the
  // columns an upload needs; with none shown, the headers and an example row.
  const downloadSample = () => {
    try {
      downloadCsv(sampleCsvRows(filtered, types), "Device Master - sample.csv");
    } catch (error) {
      console.error("Sample CSV download failed", error);
      messages.error("Could not build the file");
    }
  };

  const shown = pageOf(changedFirst(unsetFirst(filtered, DEVICE_SETUP_FIELDS)), page, pageSize);
  const loading = loadingCount > 0;
  // While stores' devices are loading the actions wait, since they would work
  // on a partial list. Hovering one says why (see DisabledHint).
  const columns = Object.values(COLUMNS);
  // Why Save can't be pressed right now, if it can't.
  const saveHint = loading
    ? LOADING_TITLE
    : saveCount === 0 && !saving
      ? "Nothing to save yet. Change a device type or To be scheduled, or upload a CSV, first."
      : null;

  return (
    <div style={{ ...styles.container }}>
      <div style={{ ...styles.rightContainer }}>
        <Header />

        <div className="rp-page">
          <div className="rp-title">Device Master</div>
          <div className="rp-subtitle">
            {selectedStores.length === 0
              ? "Pick stores to set each device's type and whether it is scheduled"
              : loading
                ? "Loading devices..."
                : `${plural(rows.length, "device")} across ${plural(selectedStores.length, "store")}`}
          </div>

          <div className="rp-filters">
            <div className="rp-field">
              <span className="rp-label">Region</span>
              <MultiSelect
                options={regionOptionList}
                value={selectedRegions}
                onChange={setSelectedRegions}
                summary={{ empty: "All regions", nouns: "regions" }}
                noOptionsMessage={() => "No regions"}
                width="200px"
              />
            </div>

            <div className="rp-field">
              <span className="rp-label">Stores</span>
              <MultiSelect
                options={storeOptions}
                value={selectedStores}
                onChange={setSelectedStores}
                summary={{ empty: "Select stores...", nouns: "stores" }}
                formatOptionLabel={storeOptionLabel}
                onMenuOpen={refreshStoreList}
                isLoading={storesRefreshing || loading}
                noOptionsMessage={() =>
                  selectedRegions.length ? "No stores in these regions" : "No stores"
                }
                width="260px"
              />
            </div>

            <div className="rp-field">
              <span className="rp-label">Search</span>
              <input
                className="dm-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Store, device id, name or type"
                disabled={rows.length === 0}
              />
            </div>

            <DisabledHint message={loading && LOADING_TITLE}>
              <button
                type="button"
                className="rp-action dm-secondary"
                onClick={refresh}
                disabled={selectedStores.length === 0 || loading}
                title="Re-read the selected stores' devices"
              >
                {loading ? "Loading..." : "Refresh"}
              </button>
            </DisabledHint>
          </div>

          {/* Closed by default, so the table starts higher up the page. */}
          <details className="dm-howto">
            <summary className="dm-howto-title">Update from a CSV file</summary>
            <ol className="dm-howto-steps">
              <li>
                <DisabledHint message={loading && LOADING_TITLE}>
                  <button
                    type="button"
                    className="dm-link"
                    onClick={downloadSample}
                    disabled={loading}
                  >
                    Download the sample CSV
                  </button>
                </DisabledHint>
                .{" "}
                {filtered.length > 0
                  ? `It lists the ${plural(filtered.length, "device")} shown, with their device IDs and current values filled in.`
                  : `No devices are shown, so it has the headers and one example row (${SAMPLE_DEVICE_ID}) - replace it with your device IDs. Pick stores above to get their devices filled in.`}
              </li>
              <li>Change Device Type or To be scheduled where needed. Leave the rest as they are.</li>
              <li>
                Upload it with <b>Upload CSV</b>. You'll see a preview before anything changes.
              </li>
            </ol>
            <table className="dm-howto-columns">
              <thead>
                <tr>
                  <th>Column</th>
                  <th>Required</th>
                  <th>What to enter</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <code>deviceId</code>
                  </td>
                  <td>
                    <span className="dm-req">Yes</span>
                  </td>
                  <td>The device to update. Filled in for the devices shown.</td>
                </tr>
                <tr>
                  <td>
                    <code>Device Type</code>
                  </td>
                  <td rowSpan={2}>
                    <span className="dm-req">At least one</span>
                  </td>
                  <td>One of the device types in the dropdown. Blank keeps the current type.</td>
                </tr>
                <tr>
                  <td>
                    <code>To be scheduled</code>
                  </td>
                  <td>Yes or No. Blank keeps the current value.</td>
                </tr>
              </tbody>
            </table>
          </details>

          <div className="sa-toolbar">
            <div className="sa-summary">
              {loading
                ? `Loading devices for ${plural(loadingCount, "store")}... actions are paused until it finishes`
                : editCount > 0
                ? `${plural(editCount, "unsaved change")}`
                : rows.length > 0
                  ? "No unsaved changes"
                  : ""}
            </div>
            <div className="dm-actions">
              {rows.length > 0 && (
                <DisabledHint message={loading && LOADING_TITLE}>
                  <button
                    type="button"
                    className="rp-action dm-secondary"
                    onClick={download}
                    disabled={loading}
                    title="The current rows, in the format Upload CSV reads back"
                  >
                    Download CSV
                  </button>
                </DisabledHint>
              )}
              <DisabledHint message={loading && LOADING_TITLE}>
                <button
                  type="button"
                  className="rp-action dm-secondary"
                  onClick={downloadSample}
                  disabled={loading}
                  title={
                    filtered.length
                      ? `deviceId, Device Type and To be scheduled for the ${plural(filtered.length, "device")} shown`
                      : "The headers and an example row; pick stores to fill in their devices"
                  }
                >
                  Sample CSV
                </button>
              </DisabledHint>
              <DisabledHint message={loading && LOADING_TITLE}>
                <button
                  type="button"
                  className="rp-action dm-secondary"
                  onClick={() => fileInput.current?.click()}
                  disabled={readingCsv || saving || loading}
                  title="Update device types and scheduling from a CSV"
                >
                  {readingCsv ? "Reading..." : "Upload CSV"}
                </button>
              </DisabledHint>
              <input ref={fileInput} type="file" accept=".csv,text/csv" hidden onChange={onFile} />
              {editCount > 0 && (
                <DisabledHint message={loading && LOADING_TITLE}>
                  <button
                    type="button"
                    className="rp-action dm-secondary"
                    onClick={() => setConfirmingDiscard(true)}
                    disabled={saving || loading}
                  >
                    Discard
                  </button>
                </DisabledHint>
              )}
              <DisabledHint message={saveHint}>
                <button
                  type="button"
                  className="rp-action rp-action-primary"
                  onClick={save}
                  disabled={saveCount === 0 || saving || loading}
                  title={`Save device type and scheduling changes for ${plural(saveCount, "device")}`}
                >
                  {saving ? "Saving..." : `Save changes${saveCount ? ` (${saveCount})` : ""}`}
                </button>
              </DisabledHint>
            </div>
          </div>

          {csvPlan && (
            <div className="dm-csv">
              <div className="dm-csv-title">
                {csvPlan.name}:{" "}
                {csvPlan.plan.updates.length
                  ? `${plural(csvPlan.plan.updates.length, "device")} will change`
                  : "nothing to change"}
                {csvPlan.plan.unchanged > 0 && `, ${csvPlan.plan.unchanged} already up to date`}
                {csvPlan.plan.errors.length > 0 &&
                  `, ${plural(csvPlan.plan.errors.length, "line")} skipped`}
              </div>

              {csvPlan.plan.updates.length > 0 && (
                <div className="dm-csv-changes">
                  {csvPlan.plan.updates.slice(0, 50).map(({ row, deviceType, toBeScheduled }) => (
                    <div key={row.deviceId} className="dm-csv-line">
                      <span className="dm-mono">{row.deviceId}</span>
                      {row.deviceName && ` (${row.deviceName})`}:{" "}
                      {deviceType !== undefined && deviceType !== row.deviceType && (
                        <span>
                          type {row.deviceType || "not set"} → <b>{deviceType}</b>{" "}
                        </span>
                      )}
                      {toBeScheduled !== undefined && toBeScheduled !== row.toBeScheduled && (
                        <span>
                          scheduled {row.toBeScheduled || "not set"} → <b>{toBeScheduled}</b>
                        </span>
                      )}
                    </div>
                  ))}
                  {csvPlan.plan.updates.length > 50 && (
                    <div className="dm-csv-line dm-muted">
                      and {csvPlan.plan.updates.length - 50} more
                    </div>
                  )}
                </div>
              )}

              {csvPlan.plan.errors.length > 0 && (
                <div className="dm-csv-errors">
                  {csvPlan.plan.errors.map((error, index) => (
                    <div key={`${error.line}-${index}`}>
                      {error.line ? `Line ${error.line}: ` : ""}
                      {error.message}
                    </div>
                  ))}
                </div>
              )}

              <div className="dm-actions" style={{ marginTop: "12px" }}>
                <DisabledHint message={loading && LOADING_TITLE}>
                  <button
                    type="button"
                    className="rp-action rp-action-primary"
                    onClick={applyCsv}
                    disabled={csvPlan.plan.updates.length === 0 || loading}
                  >
                    Apply ({csvPlan.plan.updates.length})
                  </button>
                </DisabledHint>
                <button type="button" className="rp-action dm-secondary" onClick={() => setCsvPlan(null)}>
                  Cancel
                </button>
              </div>
              <div className="dm-csv-hint">
                Required: <b>deviceId</b>, and at least one of <b>Device Type</b> or{" "}
                <b>To be scheduled</b> (Yes/No). Blank cells keep the current value. Any other column
                (DPI ID, Store Name, device Name) is optional.
              </div>
            </div>
          )}

          <ActiveFilters
            specs={COLUMN_FILTERS}
            labels={COLUMNS}
            filters={columnFilters}
            options={columnFilterOptions}
            onChange={setColumnFilter}
            onClear={() => setColumnFilters({})}
          />

          <div className="rp-table-wrap dm-table-page">
            <table className="rp-table dm-table">
              <thead>
                <tr>
                  {COLUMN_FILTERS.map(({ field, kind }) => (
                    <FilterHeaderCell
                      key={field}
                      label={COLUMNS[field]}
                      kind={kind}
                      value={columnFilters[field]}
                      options={columnFilterOptions[field]}
                      onChange={(value) => setColumnFilter(field, value)}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.rows.length === 0 ? (
                  <tr>
                    <td className="rp-empty" colSpan={columns.length}>
                      {loading
                        ? "Loading devices..."
                        : selectedStores.length === 0
                          ? "Pick one or more stores to list their devices."
                          : rows.length === 0
                            ? "No devices in the selected stores."
                            : search.trim()
                              ? `Nothing matches "${search}"${filterCount > 0 ? " with these filters" : ""}.`
                              : "Nothing matches these filters."}
                    </td>
                  </tr>
                ) : (
                  shown.rows.map((row) => (
                    <tr
                      key={row.deviceId}
                      className={
                        row.changed
                          ? "dm-row-changed"
                          : unsetCount(row, DEVICE_SETUP_FIELDS) > 0
                            ? "dm-row-unset"
                            : undefined
                      }
                    >
                      <td>{row.storeCode}</td>
                      <td className="dm-store">{row.storeName}</td>
                      <td className="dm-mono">
                        {row.deviceId}
                        {row.changed ? (
                          <span className="dm-pending-tag">Unsaved</span>
                        ) : (
                          unsetCount(row, DEVICE_SETUP_FIELDS) > 0 && <span className="dm-unset-tag">Not set</span>
                        )}
                      </td>
                      <td>{row.deviceName || <span className="dm-muted">Unnamed</span>}</td>
                      <td>
                        <Select
                          options={typeSelectOptions}
                          value={row.deviceType ? { value: row.deviceType, label: row.deviceType } : null}
                          onChange={(option) => option && updateRow(row, "deviceType", option.value)}
                          isLoading={categories === null && !categoriesError}
                          noOptionsMessage={() =>
                            categoriesError ? "Device types could not be loaded" : "No device types"
                          }
                          placeholder="Select type..."
                          menuPortalTarget={document.body}
                          menuPlacement="auto"
                          styles={cellSelectStyles("210px")}
                          isDisabled={loading}
                          aria-label={`Device type for ${row.deviceId}`}
                        />
                      </td>
                      <td>
                        <Select
                          options={SCHEDULE_SELECT_OPTIONS}
                          value={
                            row.toBeScheduled
                              ? { value: row.toBeScheduled, label: row.toBeScheduled }
                              : null
                          }
                          onChange={(option) => option && updateRow(row, "toBeScheduled", option.value)}
                          isSearchable={false}
                          placeholder="Select..."
                          menuPortalTarget={document.body}
                          menuPlacement="auto"
                          styles={cellSelectStyles("130px")}
                          isDisabled={loading}
                          aria-label={`To be scheduled for ${row.deviceId}`}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {filtered.length > 0 && (
            <div className="sa-pager">
              <span>Rows per page</span>
              <Select
                options={PAGE_SIZE_OPTIONS}
                value={PAGE_SIZE_OPTIONS.find((option) => option.value === pageSize)}
                onChange={(option) => {
                  setPage(Math.floor(shown.start / option.value) + 1);
                  setPageSize(option.value);
                }}
                isSearchable={false}
                menuPlacement="auto"
                styles={selectStyles("120px")}
              />
              <span>
                Rows {shown.start + 1}-{shown.start + shown.rows.length} of {filtered.length}
              </span>
              <button
                type="button"
                className="rp-action"
                onClick={() => setPage(shown.page - 1)}
                disabled={shown.page === 1}
              >
                Previous
              </button>
              <span>
                Page {shown.page} of {shown.pages}
              </span>
              <button
                type="button"
                className="rp-action"
                onClick={() => setPage(shown.page + 1)}
                disabled={shown.page === shown.pages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmingDiscard}
        tone="danger"
        title="Discard unsaved changes?"
        message={`${plural(editCount, "device")} ${
          editCount === 1 ? "has" : "have"
        } changes to device types or scheduling that haven't been saved. Discarding puts ${
          editCount === 1 ? "it" : "them"
        } back to the saved values. This can't be undone.`}
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        onConfirm={discard}
        onCancel={() => setConfirmingDiscard(false)}
      />
    </div>
  );
}

export default DeviceMaster;
